import { AutoAwesome, Close, Troubleshoot } from "@mui/icons-material";
import { IconButton, Tab, Tabs } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { CaptionedLoader } from "@ryde/controls";
import { ActionsEnum, useHasPermissions, useSystemParams } from "@ryde/shared";

import { InvestigationAskAi } from "./ask-ai/investigation.ask-ai";
import { PANEL_TAB_ICON_SX, TAB_SX, TABS_SX } from "./investigation.constants";
import {
  PanelLoaderStyled,
  PanelMessageStyled,
  PanelTabHostStyled,
  PanelTabsBarStyled,
} from "./investigation.panel.styled";
import {
  PanelHeaderStyled,
  PanelHeaderTitleStyled,
  PanelScrollStyled,
} from "./investigation.workspace.styled";
import { InvestigationAnomalies } from "./parts/investigation.anomalies";
import { InvestigationEventFeed } from "./parts/investigation.event-feed";
import { InvestigationHistoryFeed } from "./parts/investigation.history-feed";
import { InvestigationPassengerList } from "./parts/investigation.passenger-list";
import { InvestigationPassengerStats } from "./parts/investigation.passenger-stats";
import { InvestigationStationList } from "./parts/investigation.station-list";
import { InvestigationSummary } from "./parts/investigation.summary";
import { InvestigationTracing } from "./parts/investigation.tracing";
import { usePlaybackContext } from "./playback/ride-investigation-playback.context";
import { useInvestigationPanelTab } from "./use-investigation-panel-tab";
import { useRideInvestigationHistory } from "./use-ride-investigation-history";

/**
 * In-flow info panel (right column of the investigation split): summary / stations / events / history /
 * passengers / tracing, plus Ask AI where the company has the AI features turned on. History needs audit-log
 * read, the same as the ride's history drawer, and is where a ride without GPS opens.
 */
export const InvestigationPanel: FC = () => {
  const { model, loading, error, close, isOpen, isPlaying } =
    usePlaybackContext();
  const { t } = useTranslation();
  const { enableAIFeatures } = useSystemParams();
  const { hasPermissions: canReadHistory } = useHasPermissions({
    entity: "auditLog",
    action: ActionsEnum.Read,
  });
  const { tab, onTabChange } = useInvestigationPanelTab({
    model,
    isOpen,
    isPlaying,
    canReadHistory,
  });
  const history = useRideInvestigationHistory({
    rideId: isOpen ? model?.rideId : undefined,
    isEnabled: canReadHistory && tab === "history",
  });

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <PanelHeaderStyled>
        <PanelHeaderTitleStyled>
          <Troubleshoot fontSize="small" />
          {t("rideMap.investigation.title")}
        </PanelHeaderTitleStyled>
        <IconButton
          size="small"
          onClick={() => close()}
          aria-label={t("rideMap.investigation.close")}
        >
          <Close />
        </IconButton>
      </PanelHeaderStyled>

      {/* Anomaly banner + tabs are pinned above the scroll area (never scroll, no sticky gap). */}
      {model && <InvestigationAnomalies model={model} />}
      {model && (
        <PanelTabsBarStyled>
          <Tabs
            value={tab}
            onChange={onTabChange}
            variant="fullWidth"
            sx={TABS_SX}
          >
            {enableAIFeatures && (
              <Tab
                value="askAi"
                label={t("rideMap.investigation.tabs.askAi")}
                icon={<AutoAwesome sx={PANEL_TAB_ICON_SX} />}
                iconPosition="start"
                sx={TAB_SX}
              />
            )}
            <Tab
              value="summary"
              label={t("rideMap.investigation.tabs.summary")}
              sx={TAB_SX}
            />
            <Tab
              value="stations"
              label={t("rideMap.investigation.tabs.stations")}
              sx={TAB_SX}
            />
            <Tab
              value="events"
              label={t("rideMap.investigation.tabs.events")}
              sx={TAB_SX}
            />
            {canReadHistory && (
              <Tab
                value="history"
                label={t("rideMap.investigation.tabs.history")}
                sx={TAB_SX}
              />
            )}
            <Tab
              value="passengers"
              label={t("rideMap.investigation.tabs.passengers")}
              sx={TAB_SX}
            />
            <Tab
              value="tracing"
              label={t("rideMap.investigation.tabs.tracing")}
              sx={TAB_SX}
            />
          </Tabs>
        </PanelTabsBarStyled>
      )}

      <PanelScrollStyled>
        {loading && (
          <PanelLoaderStyled>
            <CaptionedLoader caption={t("rideMap.investigation.loading")} />
          </PanelLoaderStyled>
        )}
        {!loading && error && (
          <PanelMessageStyled>
            {t("rideMap.investigation.error")}
          </PanelMessageStyled>
        )}
        {!loading && !error && !model && (
          <PanelMessageStyled>
            {t("rideMap.investigation.noData")}
          </PanelMessageStyled>
        )}

        {model && (
          <>
            {tab === "summary" && <InvestigationSummary model={model} />}
            {tab === "stations" && <InvestigationStationList model={model} />}
            {tab === "events" && <InvestigationEventFeed model={model} />}
            {canReadHistory && tab === "history" && (
              <InvestigationHistoryFeed
                key={model.rideId}
                model={model}
                history={history.history}
                isLoading={history.isLoading}
                hasError={history.hasError}
                onRefresh={history.refresh}
              />
            )}
            {tab === "passengers" && (
              <>
                <InvestigationPassengerStats model={model} />
                <InvestigationPassengerList model={model} />
              </>
            )}
            {tab === "tracing" && <InvestigationTracing model={model} />}
            {enableAIFeatures && (
              <PanelTabHostStyled $active={tab === "askAi"}>
                <InvestigationAskAi rideId={model.rideId} />
              </PanelTabHostStyled>
            )}
          </>
        )}
      </PanelScrollStyled>
    </>
  );
};
